import {
  TokenAnalytics,
  SSEResponse,
  SSEResponseTypes,
  GeneratedFile,
  ResponseTimeAnalytics,
  ModelSelected,
  AssistantPositionRemainingType,
} from '../models/websockets.types';
import { SearchSource, Source } from 'features/chat/types/chat';
import { normalizeSubagentStreamEventType } from 'features/chat/utils/parse-message-subagent-runs';

const normalizeResponseType = (rawType: string): SSEResponseTypes => {
  const subagentStatus = normalizeSubagentStreamEventType(rawType);
  if (subagentStatus === 'started') {
    return SSEResponseTypes.SUBAGENT_STARTED;
  }
  if (subagentStatus === 'completed') {
    return SSEResponseTypes.SUBAGENT_COMPLETED;
  }
  return rawType as SSEResponseTypes;
};

const unwrapAssistantStreamedResponse = (parsedResponse: any): any => {
  const inner = parsedResponse?.streamingResponse;
  if (parsedResponse?.type !== 'ASSISTANT_STREAMED_RESPONSE' || !inner?.type) {
    return parsedResponse;
  }

  const message = parsedResponse.response?.message;
  return {
    ...inner,
    conversation_id:
      parsedResponse.response?.conversationId ??
      parsedResponse.conversation_id ??
      inner.conversation_id,
    assistant_message_id:
      message?.id ?? parsedResponse.assistant_message_id ?? inner.assistant_message_id,
    messageSenderId:
      message?.metadata?.relatedUserMessageIds?.[0] ??
      parsedResponse.messageSenderId ??
      parsedResponse.message_sender_id,
    parentIds:
      message?.parentIds ??
      parsedResponse.parentIds ??
      parsedResponse.parent_ids ??
      inner.parentIds ??
      inner.parent_ids,
    reason: parsedResponse.reason ?? parsedResponse.stream_reason ?? inner.reason,
  };
};

const SSEResponseParser = (parsedResponse: any): SSEResponse => {
  const frame = unwrapAssistantStreamedResponse(parsedResponse);
  let responseType: SSEResponseTypes;
  try {
    responseType = normalizeResponseType(String(frame.type ?? ''));
  } catch (error) {
    console.error('Error parsing websocket response type:', error, parsedResponse);
    throw new Error('Invalid response type');
  }
  const finalResponse = {
    type: responseType,
    data: frame.data,
    message_sender_id: frame.messageSenderId ?? frame.message_sender_id,
    conversation_id: frame.conversation_id || frame?.conversationId,
    ...(frame.file_names && { file_names: frame.file_names }),
    ...((frame.assistant_message_id || frame.assistantMessageId) && {
      assistant_message_id: frame.assistant_message_id || frame.assistantMessageId,
    }),
    parent_ids: frame.parentIds ?? frame.parent_ids,
    ...((frame.stream_reason || frame.reason) && {
      stream_reason: frame.stream_reason || frame.reason,
    }),
  };

  // console.log('finalResponse in SSEResponseParser', finalResponse);

  switch (responseType) {
    case SSEResponseTypes.SERVER_ERROR:
      // Agent/server errors often send `{ type, error }` without `data`.
      finalResponse.data = (frame.error ?? frame.data ?? '') as string;
      if (typeof frame.category === 'string') {
        finalResponse.category = frame.category;
      }
      if (typeof frame.code === 'string') {
        finalResponse.code = frame.code;
      }
      break;
    case SSEResponseTypes.CODE_EXECUTION_STARTED:
    case SSEResponseTypes.CODE_EXECUTION_STEP:
    case SSEResponseTypes.LLM_ANSWER:
    case SSEResponseTypes.SEARCH_RESPONSE:
    case SSEResponseTypes.LLM_THINKING:
    case SSEResponseTypes.CONVERSATION_TITLE:
    case SSEResponseTypes.END_OF_STREAM:
    case SSEResponseTypes.STREAM_CANCELLED:
    case SSEResponseTypes.IMAGE_GENERATION_STARTED:
    case SSEResponseTypes.FILE_GENERATION_STARTED:
    case SSEResponseTypes.IMAGE_GENERATION_FAILED:
    case SSEResponseTypes.FILE_GENERATION_FAILED:
    case SSEResponseTypes.CONTEXT_WINDOW_EXCEEDED:
    case SSEResponseTypes.IMAGE_GENERATION_LIMIT_EXCEEDED:
      finalResponse.data = frame.data as string;
      break;
    case SSEResponseTypes.TOKEN_ANALYTICS:
      finalResponse.data = frame.data as TokenAnalytics;
      break;
    case SSEResponseTypes.FILE_GENERATION_ON_PROGRESS:
    case SSEResponseTypes.IMAGE_GENERATION_SUCCESS:
    case SSEResponseTypes.FILE_GENERATION_SUCCESS:
    case SSEResponseTypes.CODE_EXECUTION_ARTIFACT:
      finalResponse.data = frame.data as GeneratedFile;
      break;
    case SSEResponseTypes.RESPONSE_SOURCES:
      finalResponse.data = frame.data as Source[];
      break;
    case SSEResponseTypes.SEARCH_SOURCES:
      finalResponse.data = frame.data as SearchSource[];
      break;
    case SSEResponseTypes.ASSISTANT_REQUEST_PROCESSING_STARTED:
    case SSEResponseTypes.ASSISTANT_REQUEST_QUEUED:
      finalResponse.data = frame.data as AssistantPositionRemainingType;
      break;
    case SSEResponseTypes.RESPONSE_TIME_ANALYTICS:
      finalResponse.data = frame.data as ResponseTimeAnalytics;
      break;
    case SSEResponseTypes.MODEL_SELECTED:
      finalResponse.data = frame.data as ModelSelected;
      break;
    case SSEResponseTypes.TOOL_CALL_STARTED:
    case SSEResponseTypes.TOOL_CALL_COMPLETED:
    case SSEResponseTypes.SKILL_USED:
    case SSEResponseTypes.SKILL_SKIPPED:
    case SSEResponseTypes.SKILL_RESOURCE_LOADED:
    case SSEResponseTypes.SUBAGENT_STARTED:
    case SSEResponseTypes.SUBAGENT_COMPLETED:
    case SSEResponseTypes.AUTH_REQUIRED:
    case SSEResponseTypes.A2UI_MESSAGE:
    case SSEResponseTypes.APPROVAL_REQUIRED:
    case SSEResponseTypes.APPROVAL_RESOLVED:
    case SSEResponseTypes.CONVERSATION_COMPACTION_STARTED:
    case SSEResponseTypes.CONVERSATION_COMPACTION_COMPLETED:
    case SSEResponseTypes.CONVERSATION_COMPACTION_FAILED:
    case SSEResponseTypes.PLATFORM_RESOURCE_CHANGED:
      finalResponse.data = frame.data;
      break;
    case SSEResponseTypes.CONTENT_FILTER_VIOLATION:
      if (typeof frame.data === 'string') {
        finalResponse.data = frame.data;
      } else if (frame.data && typeof frame.data === 'object') {
        finalResponse.data =
          (frame.data.refusal as string) ||
          (frame.data.message as string) ||
          (frame.data.content as string) ||
          '';
      } else {
        finalResponse.data = ((frame.refusal ?? frame.error ?? frame.message ?? '') as string);
      }
      break;
    default:
      throw new Error(`Unhandled response type: ${String(frame.type)}`);
  }
  return finalResponse as SSEResponse;
 };
 
 export const WebsocketServices = {
  SSEResponseParser,
 };
 
 
